/**
 * The Scheduler role's part of Settings > Texting agent > Per-study
 * (docs/plans/crm-agent-scheduler-mode-plan.md B10 SETUP PROCESS, B12
 * frontend; WP-10). Rendered only for a study whose role is Scheduler:
 *
 *   - the owner's rules, in plain words (everyone on the study's recruiter
 *     list can be booked; whoever takes a call gets the lead; replies wait for
 *     8 AM except the welcome and the quiet first-hour booking);
 *   - the study's recruiter list, READ-ONLY (it is edited on the study's
 *     Recruiters tab), with each bookable member's calendar AGGREGATES only
 *     (blocking events in the next 14 days, days off; personal events stay
 *     private) and the no-activity warnings with their upcoming
 *     assistant-booked calls. There is no tick box: everyone listed is
 *     bookable (owner decision 17);
 *   - the optional per-study clinic address override (the address itself is
 *     saved per site in Booking settings), the owner-offer tolerance and the
 *     study's call length (empty = Booking settings' slot length);
 *   - the 30 scheduler lines with their publish state and whether each blocks
 *     switching on.
 * Everything comes from the readiness response's `scheduler` detail and the
 * config row; saves go through the page's config PUT (merged on the server).
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, CalendarClock, CheckCircle2, ExternalLink, MapPin, Users } from 'lucide-react';
import { fieldCls } from '../filters';
import { SCHEDULER_LINES, lineRequirement, schedLineKey } from './schedulerLogic';

export interface SchedulerRosterMember {
  user_id: string;
  name: string | null;
  live: boolean;
  not_live_reason?: 'deleted' | 'deactivated' | 'no_crm_access' | null;
  blocking_events_14d?: number;
  absence_dates?: string[];
  active?: boolean;
  calendar_activity?: boolean;
  warnings?: string[];
  upcoming_assistant_calls?: Array<{ callback_id: string; contact_id: string; scheduled_at: string; label: string }>;
}

export interface SchedulerReadinessDetail {
  role: 'scheduler';
  lines: Array<{ line: string; key: string; published: boolean; required: 'yes' | 'with_address' | 'never' }>;
  roster: {
    members: SchedulerRosterMember[];
    calendar_read_failed?: boolean;
    absence_read_failed?: boolean;
    activity_read_failed?: boolean;
  } | null;
  clinic_address: { address: string | null; override: string | null; matches_footer?: boolean; footer?: string | null };
  closed_dates_next_90: number | null;
  timezone: string | null;
  /** This study's call length (null = unset) and Booking hours' slot length, the default. */
  call_length?: { minutes: number | null; default_minutes: number };
}

const NOT_LIVE: Record<string, string> = {
  deleted: 'account removed',
  deactivated: 'account deactivated',
  no_crm_access: 'no CRM access',
};

/** 'Oct 5' for a 'YYYY-MM-DD' key. */
const shortDay = (key: string) => {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: 'UTC' });
};

export default function SchedulerStudySettings({
  studyId, textingAgent, scheduler, busy, onSaveTextingAgent,
}: {
  studyId: string;
  textingAgent: { clinic_address?: string | null; owner_offer_tolerance_min?: number | null; call_length_min?: number | null };
  scheduler: SchedulerReadinessDetail | null | undefined;
  busy: boolean;
  /** Saves config.texting_agent keys (null clears one); resolves true on success. */
  onSaveTextingAgent: (keys: Record<string, unknown>) => Promise<boolean>;
}) {
  const [address, setAddress] = useState(textingAgent.clinic_address || '');
  const [tolerance, setTolerance] = useState(
    textingAgent.owner_offer_tolerance_min == null ? '' : String(textingAgent.owner_offer_tolerance_min),
  );
  useEffect(() => { setAddress(textingAgent.clinic_address || ''); }, [textingAgent.clinic_address]);
  useEffect(() => {
    setTolerance(textingAgent.owner_offer_tolerance_min == null ? '' : String(textingAgent.owner_offer_tolerance_min));
  }, [textingAgent.owner_offer_tolerance_min]);

  const storedLength = textingAgent.call_length_min == null ? '' : String(textingAgent.call_length_min);
  const [callLength, setCallLength] = useState(storedLength);
  useEffect(() => { setCallLength(storedLength); }, [storedLength]);

  const members = scheduler?.roster?.members || [];
  const resolved = scheduler?.clinic_address?.address || null;
  const defaultLength = scheduler?.call_length?.default_minutes ?? null;
  const addressDirty = address.trim() !== (textingAgent.clinic_address || '');
  const toleranceDirty = tolerance.trim() !== (textingAgent.owner_offer_tolerance_min == null ? '' : String(textingAgent.owner_offer_tolerance_min));
  const lengthDirty = callLength.trim() !== storedLength;
  const lengthNum = callLength.trim() === '' ? null : Number(callLength.trim());
  const lengthInvalid = lengthNum !== null && (!Number.isInteger(lengthNum) || lengthNum < 10 || lengthNum > 240);

  const saveTolerance = async () => {
    const t = tolerance.trim();
    const n = t === '' ? null : Number(t);
    if (n !== null && (!Number.isInteger(n) || n < 0 || n > 480)) {
      setTolerance(textingAgent.owner_offer_tolerance_min == null ? '' : String(textingAgent.owner_offer_tolerance_min));
      return;
    }
    await onSaveTextingAgent({ owner_offer_tolerance_min: n });
  };

  const saveCallLength = async () => {
    if (!lengthDirty || lengthInvalid) return;
    await onSaveTextingAgent({ call_length_min: lengthNum });
  };

  const btn = 'inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0';

  return (
    <div className="mt-4 space-y-4 border-t border-gray-100 pt-4 dark:border-gray-700">
      {/* The owner's rules, in plain words (plan B10). */}
      <div className="rounded-lg border border-primary-200 bg-primary-50/50 p-3 dark:border-primary-900/50 dark:bg-primary-900/10">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold text-gray-900 dark:text-gray-100">
          <CalendarClock className="h-4 w-4 text-primary-600 dark:text-primary-400" /> How the scheduling assistant works
        </h3>
        <ul className="mt-1.5 list-disc space-y-1 pl-5 text-xs leading-relaxed text-gray-700 dark:text-gray-300">
          <li>
            <span className="font-medium">Everyone on this study&apos;s recruiter list can be booked by the assistant.</span>{' '}
            Keep the list to people who take calls, and keep their meetings and days off in the CRM calendar: the assistant
            cannot see Google or Outlook, and an empty CRM calendar looks free all day.
          </li>
          <li>People who used the CRM in the last 14 days are picked first, and the team is alerted before a call nobody seems ready to make.</li>
          <li>The lead&apos;s own recruiter takes the call when free. When someone else takes it, the lead moves to them, so they get the credit.</li>
          <li>
            The welcome goes out right away, even at night. Replies wait for 8:00 AM, except a request for the first hour of the
            next morning, which is booked quietly overnight and confirmed at 8:00 AM.
          </li>
          <li>It answers where the clinic is and how long the call takes. Every other study question waits for the call.</li>
          <li>It sends no links, unless the lead has trouble booking by text or asks for one.</li>
        </ul>
      </div>

      {/* The recruiter list, read-only (owner decision 17: no tick box). */}
      <div>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="flex items-center gap-1.5 text-sm font-semibold text-gray-900 dark:text-gray-100">
            <Users className="h-4 w-4 text-primary-600 dark:text-primary-400" /> Who the assistant can book
          </h3>
          <Link
            to={`/crm/settings/studies/${studyId}?tab=recruiters`}
            className="text-xs font-medium text-primary-600 hover:underline dark:text-primary-400"
          >
            Edit the list on the study&apos;s Recruiters tab
          </Link>
        </div>
        {!scheduler?.roster ? (
          <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">The recruiter list could not be read right now (see the checklist).</p>
        ) : members.length === 0 ? (
          <p className="mt-1.5 rounded-lg border border-dashed border-gray-300 p-3 text-center text-xs text-gray-500 dark:border-gray-600 dark:text-gray-400">
            Nobody is on this study&apos;s recruiter list, so the assistant cannot book calls.
          </p>
        ) : (
          <div className="mt-1.5 overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-xs dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800/50">
                  <tr>
                    <th scope="col" className="px-3 py-2 text-left font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Recruiter</th>
                    <th scope="col" className="px-3 py-2 text-left font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Calendar blocks (14 days)</th>
                    <th scope="col" className="px-3 py-2 text-left font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Days off</th>
                    <th scope="col" className="px-3 py-2 text-left font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Check</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {members.map((m) => {
                    const warn = (m.warnings || []).length > 0;
                    return (
                      <tr key={m.user_id} className="align-top">
                        <td className="px-3 py-2 font-medium text-gray-900 dark:text-gray-100">
                          {m.name || 'Unnamed user'}
                          {!m.live && (
                            <span className="ml-1.5 rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                              not bookable: {NOT_LIVE[m.not_live_reason || ''] || 'unavailable'}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 tabular-nums text-gray-700 dark:text-gray-300">{m.live ? (m.blocking_events_14d ?? 0) : '—'}</td>
                        <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
                          {m.live ? ((m.absence_dates || []).length ? m.absence_dates!.map(shortDay).join(', ') : 'none') : '—'}
                        </td>
                        <td className="px-3 py-2">
                          {!m.live ? (
                            <span className="text-gray-400 dark:text-gray-500">—</span>
                          ) : warn ? (
                            <div className="text-amber-700 dark:text-amber-400">
                              <span className="inline-flex items-start gap-1 font-medium">
                                <AlertTriangle className="mt-0.5 h-3 w-3 flex-shrink-0" />
                                {[
                                  (m.warnings || []).includes('no_activity') && 'No CRM activity in 14 days',
                                  (m.warnings || []).includes('no_calendar') && 'No CRM calendar activity',
                                ].filter(Boolean).join('; ')}
                              </span>
                              <span className="block text-gray-600 dark:text-gray-400">
                                The assistant can book {m.name || 'them'}, and an empty CRM calendar looks free all day.
                              </span>
                              {(m.upcoming_assistant_calls || []).length > 0 && (
                                <ul className="mt-0.5 space-y-0.5">
                                  {m.upcoming_assistant_calls!.map((c) => (
                                    <li key={c.callback_id}>
                                      <Link to={`/crm/contacts/${c.contact_id}`} className="text-primary-700 hover:underline dark:text-primary-400">
                                        Assistant call {c.label}
                                      </Link>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-green-700 dark:text-green-400">
                              <CheckCircle2 className="h-3 w-3" /> Uses the CRM and its calendar
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {(scheduler?.roster?.activity_read_failed || scheduler?.roster?.calendar_read_failed || scheduler?.roster?.absence_read_failed) && (
          <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">Some calendar or activity data could not be read, so a warning may be missing.</p>
        )}
      </div>

      {/* The clinic address override and the owner-offer tolerance. */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <div>
          <h3 className="flex items-center gap-1.5 text-sm font-semibold text-gray-900 dark:text-gray-100">
            <MapPin className="h-4 w-4 text-primary-600 dark:text-primary-400" /> Clinic address
          </h3>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            {resolved
              ? <>Texts give <span className="font-medium text-gray-700 dark:text-gray-200">{resolved}</span>.</>
              : <>No address resolves yet, so &quot;where are you located?&quot; gets the fixed deflection.</>}
            {' '}The address is saved once per site in{' '}
            <Link to="/crm/settings/booking" className="font-medium text-primary-600 hover:underline dark:text-primary-400">Booking settings</Link>.
            Set an override only when this study needs a different one (a suite or an entrance).
          </p>
          <label htmlFor="sched-clinic-override" className="mt-2 block text-xs font-medium text-gray-600 dark:text-gray-300">Address override (optional)</label>
          <div className="mt-0.5 flex flex-wrap items-center gap-2">
            <input
              id="sched-clinic-override"
              value={address}
              maxLength={150}
              disabled={busy}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="4218-C Arendell Street, Morehead City, NC 28557"
              className={`${fieldCls} min-w-0 flex-1`}
            />
            <button type="button" className={btn} disabled={busy || !addressDirty} onClick={() => onSaveTextingAgent({ clinic_address: address.trim() || null })}>
              Save
            </button>
            {textingAgent.clinic_address && (
              <button type="button" className={btn} disabled={busy} onClick={() => onSaveTextingAgent({ clinic_address: null })}>
                Clear
              </button>
            )}
          </div>
        </div>
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Keep offers near the lead&apos;s recruiter</h3>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            When the assistant picks times to offer, the lead&apos;s own recruiter&apos;s times come first if they are within this many
            minutes of the earliest open time, so leads do not change hands for nothing. Empty uses the default (120).
          </p>
          <div className="mt-2 flex items-center gap-2">
            <input
              id="sched-owner-tolerance"
              aria-label="Owner offer tolerance in minutes"
              type="number"
              inputMode="numeric"
              min={0}
              max={480}
              value={tolerance}
              disabled={busy}
              onChange={(e) => setTolerance(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') saveTolerance(); }}
              className={`${fieldCls} w-24 tabular-nums`}
            />
            <span className="text-xs text-gray-500 dark:text-gray-400">minutes</span>
            <button type="button" className={btn} disabled={busy || !toleranceDirty} onClick={saveTolerance}>Save</button>
          </div>
        </div>
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Call length</h3>
          <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
            How long the assistant books this study&apos;s calls for, and what it tells the lead when they ask. Offered start times
            stay on the{' '}
            <Link to="/crm/settings/booking" className="font-medium text-primary-600 hover:underline dark:text-primary-400">Booking settings</Link>
            {' '}grid. Empty uses the slot length there{defaultLength != null ? ` (${defaultLength} minutes)` : ''}.
            Calls a recruiter books by hand are not affected.
          </p>
          <div className="mt-2 flex items-center gap-2">
            <input
              id="sched-call-length"
              aria-label="Call length in minutes"
              type="number"
              inputMode="numeric"
              min={10}
              max={240}
              value={callLength}
              disabled={busy}
              placeholder={defaultLength != null ? String(defaultLength) : ''}
              onChange={(e) => setCallLength(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') saveCallLength(); }}
              aria-invalid={lengthInvalid || undefined}
              className={`${fieldCls} w-24 tabular-nums`}
            />
            <span className="text-xs text-gray-500 dark:text-gray-400">minutes</span>
            <button type="button" className={btn} disabled={busy || !lengthDirty || lengthInvalid} onClick={saveCallLength}>Save</button>
          </div>
          {lengthInvalid && (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">Enter a whole number of minutes from 10 to 240, or leave it empty.</p>
          )}
        </div>
      </div>

      {/* The 30 scheduler lines (plan B11). */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Scheduler lines</h3>
        <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
          Every text the assistant sends is one of these lines, with the times, names and address filled in by code.
          Publishing a line in its editor is the owner&apos;s approval. 28 must be published before the study can switch on;
          the clinic address line once an address is set; the &quot;what number we call from&quot; line only after the pilot&apos;s caller-ID check.
        </p>
        <ul className="mt-2 grid grid-cols-1 gap-1.5 sm:grid-cols-2 xl:grid-cols-3">
          {SCHEDULER_LINES.map(({ line, label }) => {
            const state = scheduler?.lines?.find((l) => l.line === line);
            const req = state?.required || lineRequirement(line, Boolean(resolved));
            return (
              <li key={line}>
                <Link
                  to={`/crm/settings/prompts/${encodeURIComponent(schedLineKey(line))}`}
                  className="inline-flex min-h-[44px] w-full items-center justify-between gap-2 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 transition-colors duration-150 hover:border-gray-300 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
                >
                  <span className="min-w-0 truncate" title={label}>
                    {label}
                    {req === 'never' && <span className="ml-1 text-[10px] text-gray-400">(after the pilot)</span>}
                    {req === 'with_address' && <span className="ml-1 text-[10px] text-gray-400">(needs an address)</span>}
                  </span>
                  <span className="inline-flex flex-shrink-0 items-center gap-1.5">
                    {state && (
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${state.published
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                        : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'}`}
                      >
                        {state.published ? 'Published' : 'Draft'}
                      </span>
                    )}
                    <ExternalLink className="h-3.5 w-3.5 text-gray-400" />
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
